import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { ProductHit } from '@libs/algolia';
import { getAlgoliaProductById } from '@libs/api-proxy-client';
import { getLocale } from '@libs/core';

const PRODUCT_QUERY_KEY_PREFIX = 'product-cache';
const CACHE_EXPIRATION_MS = 2 * 60 * 60 * 1000; // 2 hours

/**
 * Query key factory for product cache queries
 */
export const productQueryKey = (sku: string) => [PRODUCT_QUERY_KEY_PREFIX, sku] as const;

/**
 * Hook to fetch a single product using TanStack Query
 * Uses server-side cached endpoint from DP-2245 (product-catalog-process-api)
 * TanStack Query provides additional client-side caching layer
 */
export const useProductQuery = (sku: string | undefined, enabled = true) => {
    return useQuery({
        queryKey: productQueryKey(sku ?? ''),
        queryFn: async () => {
            if (!sku) {
                throw new Error('SKU is required');
            }
            const locale = getLocale();
            const productHit = await getAlgoliaProductById({
                id: sku,
                locale,
                caller: 'web:account:useProductQuery-client',
            });
            if (!productHit) {
                throw new Error(`Product not found: ${sku}`);
            }
            return productHit;
        },
        enabled: Boolean(sku) && enabled,
        staleTime: CACHE_EXPIRATION_MS,
        gcTime: CACHE_EXPIRATION_MS,
        retry: false,
    });
};

/**
 * Hook to get cached product data from TanStack Query cache
 */
export const useCachedProductData = () => {
    const queryClient = useQueryClient();

    return (sku: string): ProductHit | null => {
        const cached = queryClient.getQueryData<ProductHit>(productQueryKey(sku));
        return cached ?? null;
    };
};
